import { useEffect, useState } from 'react';
import { useCountdown } from './useCountdown';
import { MAX_ATTEMPTS_PER_METHOD } from '../utils/verificationAttempts';

/**
 * Failure count for one verification method. Reaching MAX_ATTEMPTS_PER_METHOD, or a server 429,
 * locks the method for `lockMs` (the BFF lifts its ceiling after the same time); when the lock
 * runs out the count starts again and `onUnlock` runs.
 */
export function useAttemptLock(lockMs: number, onUnlock?: () => void) {
  const [attempts, setAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const lockSecondsLeft = useCountdown(lockedUntil);

  useEffect(() => {
    if (lockedUntil !== null && lockSecondsLeft === 0) {
      setLockedUntil(null);
      setAttempts(0);
      onUnlock?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedUntil, lockSecondsLeft]);

  /** Lock the method now, keeping a lock that is already running. */
  const lockOut = () => {
    setAttempts(MAX_ATTEMPTS_PER_METHOD);
    setLockedUntil((until) => until ?? Date.now() + lockMs);
  };

  const countFailure = () => {
    if (attempts + 1 >= MAX_ATTEMPTS_PER_METHOD) lockOut();
    else setAttempts(attempts + 1);
  };

  const reset = () => {
    setAttempts(0);
    setLockedUntil(null);
  };

  return { attempts, lockSecondsLeft, countFailure, lockOut, reset };
}
